import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowMarkers } from './ArrowMarkers';
import { TitleBlock } from './TitleBlock';
import { ProjectionViewport } from './ProjectionViewport';
import { BOMTable } from './BOMTable';
import { ScaleBar } from './ScaleBar';
import { BOMReviewModal } from './BOMReviewModal';
import { SHEET, COLORS, LINE_STYLES } from './constants';
import { useDrawingZoom } from '@/hooks/useDrawingZoom';
import { useDrawingSheets } from '@/hooks/useDrawingSheets';
import { useDrawingSettings } from '@/hooks/useDrawingSettings';
import { parseSvgToStructuredData } from '@/utils/svgPathParser';
import type { ProjectionState } from '@/hooks/useProjectionViews';
import { Parameter, DrawingMetadata, SectionPlane, BOMEntry } from '@shared/types';
import { Download, Maximize, FileDown, ClipboardList } from 'lucide-react';
import { exportDrawingSVG, exportDrawingSVGZip, exportDrawingPDF } from '@/utils/drawingExport';
import type { ExportOptions } from '@/utils/drawingExport';

type SectionViewState = ProjectionState & { plane: SectionPlane };

type DrawingSheetProps = {
  projections: Record<string, ProjectionState>;
  /** Component-level projections keyed as "{componentId}_{view}" */
  componentProjections?: Record<string, ProjectionState>;
  sections?: SectionViewState[];
  parameters?: Parameter[];
  objectName?: string;
  metadata?: DrawingMetadata;
  onParameterChange?: (param: Parameter, value: number) => void;
  onParameterCommit?: (param: Parameter, value: number) => void;
  onBOMUpdate?: (bom: BOMEntry[]) => void;
};

export function DrawingSheet({
  projections,
  componentProjections = {},
  sections,
  parameters = [],
  objectName,
  metadata,
  onParameterChange,
  onParameterCommit,
  onBOMUpdate,
}: DrawingSheetProps) {
  const { t } = useTranslation('common');
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [bomModalOpen, setBomModalOpen] = useState(false);

  const { settings } = useDrawingSettings();

  const sheetW = SHEET.A3.w;
  const sheetH = SHEET.A3.h;
  const margin = SHEET.margin;

  const { zoomToFit, zoomToView } = useDrawingZoom({
    containerRef,
    svgRef,
    sheetWidth: sheetW,
    sheetHeight: sheetH,
  });

  // Multi-sheet support
  const { sheets, activeSheet, setActiveSheet, totalSheets } = useDrawingSheets(metadata);
  const currentSheet = sheets.find((s) => s.sheetNumber === activeSheet) ?? sheets[0];
  const layout = currentSheet.layout;

  const exportOpts: ExportOptions = {
    title: objectName,
    author: settings.companyName || undefined,
    drawingNumberPrefix: settings.drawingNumberPrefix || undefined,
  };

  const handleExportSVG = useCallback(() => {
    if (!svgRef.current) return;
    if (totalSheets > 1) {
      // For multi-sheet, we'd need all SVG refs — for now export current sheet
      exportDrawingSVG(svgRef.current, exportOpts);
    } else {
      exportDrawingSVG(svgRef.current, exportOpts);
    }
  }, [totalSheets, exportOpts]);

  const handleExportPDF = useCallback(async () => {
    if (!svgRef.current) return;

    if (totalSheets <= 1) {
      await exportDrawingPDF(svgRef.current, 'A3', exportOpts);
      return;
    }

    // Multi-page: cycle through all sheets, clone each SVG
    const savedActiveSheet = activeSheet;
    const allSvgs: SVGSVGElement[] = [];

    for (let i = 0; i < totalSheets; i++) {
      setActiveSheet(i + 1);
      // Give React a tick to re-render the sheet
      await new Promise((r) => setTimeout(r, 100));
      if (svgRef.current) {
        allSvgs.push(svgRef.current.cloneNode(true) as SVGSVGElement);
      }
    }

    // Restore original sheet
    setActiveSheet(savedActiveSheet);

    if (allSvgs.length > 0) {
      await exportDrawingPDF(allSvgs, 'A3', exportOpts);
    }
  }, [exportOpts, totalSheets, activeSheet, setActiveSheet]);

  const handleBOMAccept = useCallback((bom: BOMEntry[]) => {
    onBOMUpdate?.(bom);
  }, [onBOMUpdate]);

  const renderMode = metadata ? 'drawing' as const : 'interactive' as const;
  const companyName = settings.companyName || 'AgentCAD';

  return (
    <div ref={containerRef} className="relative h-full w-full bg-synergy-neutral-900">
      <svg
        ref={svgRef}
        className="h-full w-full"
        style={{ cursor: 'grab' }}
      >
        <g className="sheet-content">
          {/* Sheet background */}
          <rect
            x={0}
            y={0}
            width={sheetW}
            height={sheetH}
            fill={COLORS.background}
            rx={1}
          />

          {/* Sheet border */}
          <rect
            x={margin}
            y={margin}
            width={sheetW - margin * 2}
            height={sheetH - margin * 2}
            fill="none"
            stroke={COLORS.border}
            strokeWidth={LINE_STYLES.border.strokeWidth}
          />

          {/* Inner margin */}
          <rect
            x={margin + SHEET.innerMargin}
            y={margin + SHEET.innerMargin}
            width={sheetW - (margin + SHEET.innerMargin) * 2}
            height={sheetH - (margin + SHEET.innerMargin) * 2}
            fill="none"
            stroke={COLORS.border}
            strokeWidth={LINE_STYLES.dimension.strokeWidth}
            opacity={0.3}
          />

          <ArrowMarkers />

          {/* Scale verification bar (print-ready) */}
          {renderMode === 'drawing' && (
            <ScaleBar x={margin + 15} y={sheetH - margin + 5} />
          )}

          {/* Projection viewports from layout engine */}
          {Array.from(layout.views.entries()).map(([viewName, rect]) => {
            // Check both assembly projections and component projections
            const isComponentView = viewName.includes('_') && !viewName.startsWith('section');
            const proj = isComponentView
              ? componentProjections[viewName]
              : projections[viewName];
            if (!proj) return null;
            const parsedData = proj.svgString
              ? parseSvgToStructuredData(proj.svgString)
              : null;

            // For component views, find component-specific dims/annotations
            const compId = isComponentView ? viewName.split('_').slice(0, -1).join('_') : undefined;
            const compDrawing = compId
              ? metadata?.componentDrawings?.find(c => c.componentId === compId)
              : undefined;
            const viewDimensions = compDrawing?.dimensions ?? metadata?.dimensions;
            const viewAnnotations = compDrawing?.annotations ?? metadata?.annotations;

            return (
              <g
                key={viewName}
                onClick={() => zoomToView(rect)}
                style={{ cursor: 'pointer' }}
              >
                <ProjectionViewport
                  view={viewName}
                  x={rect.x}
                  y={rect.y}
                  width={rect.w}
                  height={rect.h}
                  parsedData={parsedData}
                  isCompiling={proj.isCompiling}
                  error={proj.error}
                  parameters={parameters}
                  renderMode={renderMode}
                  sectionPlanes={isComponentView ? undefined : metadata?.sectionPlanes}
                  annotations={viewAnnotations}
                  dimensions={viewDimensions}
                  onParameterChange={onParameterChange}
                  onParameterCommit={onParameterCommit}
                />
              </g>
            );
          })}

          {/* Section viewports */}
          {sections?.map((section, i) => {
            const sectionKey = `section-${section.plane.label || i}`;
            const rect = layout.views.get(sectionKey);
            if (!rect) return null;
            const parsedData = section.svgString
              ? parseSvgToStructuredData(section.svgString)
              : null;

            return (
              <ProjectionViewport
                key={sectionKey}
                view={sectionKey}
                x={rect.x}
                y={rect.y}
                width={rect.w}
                height={rect.h}
                parsedData={parsedData}
                isCompiling={section.isCompiling}
                error={section.error}
                renderMode="drawing"
              />
            );
          })}

          {/* BOM table */}
          {layout.bomArea && metadata?.bom && metadata.bom.length > 0 && (
            <BOMTable
              x={layout.bomArea.x}
              y={layout.bomArea.y}
              width={layout.bomArea.w}
              maxHeight={layout.bomArea.h}
              bom={metadata.bom}
              accessories={metadata.accessories}
              productInfo={metadata.productInfo}
            />
          )}

          {/* Title block */}
          <TitleBlock
            x={layout.titleBlock.x}
            y={layout.titleBlock.y}
            productName={objectName}
            scale={metadata ? '1/10' : '1:1'}
            companyName={companyName}
            approvalChain={settings.approvalChain.length > 0 ? settings.approvalChain : undefined}
            notes={metadata?.productInfo?.weight
              ? `質量: ${metadata.productInfo.weight}`
              : undefined}
            sheetInfo={totalSheets > 1 ? `${activeSheet} / ${totalSheets}` : undefined}
            projectionMethod={
              settings.defaultProjectionMethod === 'first-angle'
                ? t('drawing.firstAngleProjection', { defaultValue: '第一角法' })
                : t('drawing.thirdAngleProjection', { defaultValue: '第三角法' })
            }
          />
        </g>
      </svg>

      {/* Unified bottom toolbar */}
      <div className="absolute bottom-3 right-3 z-10">
        <div className="flex items-center gap-px overflow-hidden rounded-[10px] border border-synergy-neutral-600 bg-white shadow-[0_0_6px_rgba(0,0,0,0.04)]">
          {/* Sheet navigator (if multi-sheet) */}
          {totalSheets > 1 && (
            <>
              <button
                onClick={() => setActiveSheet(Math.max(1, activeSheet - 1))}
                disabled={activeSheet <= 1}
                className="flex h-8 w-8 items-center justify-center text-synergy-text-secondary transition-colors disabled:opacity-25 [@media(hover:hover)]:hover:bg-synergy-neutral-800/8 [@media(hover:hover)]:hover:text-synergy-text-primary"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M6.5 2L3.5 5L6.5 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <span className="flex h-8 items-center px-1 font-mono text-[11px] font-medium tabular-nums text-synergy-text-primary">
                {activeSheet}<span className="mx-0.5 text-synergy-neutral-500">/</span>{totalSheets}
              </span>
              {/* Assembly vs component-detail label so the user can tell at a
                  glance which kind of sheet they're looking at. */}
              {metadata?.layoutMode === 'components' && (
                <span
                  className={`ml-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                    currentSheet?.isComponentSheet
                      ? 'bg-brand-teal text-brand-indigo'
                      : 'bg-brand-teal-soft text-brand-indigo'
                  }`}
                  title={
                    currentSheet?.isComponentSheet
                      ? t('drawing.componentSheetLabel', { defaultValue: 'Component detail' })
                      : t('drawing.assemblySheetLabel', { defaultValue: 'Assembly overview' })
                  }
                >
                  {currentSheet?.isComponentSheet
                    ? t('drawing.componentSheetBadge', { defaultValue: 'COMP' })
                    : t('drawing.assemblySheetBadge', { defaultValue: 'ASSY' })}
                </span>
              )}
              <button
                onClick={() => setActiveSheet(Math.min(totalSheets, activeSheet + 1))}
                disabled={activeSheet >= totalSheets}
                className="flex h-8 w-8 items-center justify-center text-synergy-text-secondary transition-colors disabled:opacity-25 [@media(hover:hover)]:hover:bg-synergy-neutral-800/8 [@media(hover:hover)]:hover:text-synergy-text-primary"
              >
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M3.5 2L6.5 5L3.5 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              <div className="mx-0.5 h-4 w-px bg-synergy-neutral-700/40" />
            </>
          )}

          {/* BOM button */}
          {metadata?.bom && metadata.bom.length > 0 && (
            <button
              onClick={() => setBomModalOpen(true)}
              title={t('drawing.bomReview')}
              className="flex h-8 items-center gap-1.5 px-2.5 text-[11px] font-medium text-synergy-text-secondary transition-colors [@media(hover:hover)]:hover:bg-synergy-neutral-800/8 [@media(hover:hover)]:hover:text-synergy-text-primary"
            >
              <ClipboardList className="h-3 w-3" />
              <span>BOM</span>
            </button>
          )}

          {/* Fit view */}
          <button
            onClick={zoomToFit}
            title={t('drawing.fitToView')}
            className="flex h-8 w-8 items-center justify-center text-synergy-text-secondary transition-colors [@media(hover:hover)]:hover:bg-synergy-neutral-800/8 [@media(hover:hover)]:hover:text-synergy-text-primary"
          >
            <Maximize className="h-3 w-3" />
          </button>

          <div className="mx-0.5 h-4 w-px bg-synergy-neutral-700/40" />

          {/* Export buttons */}
          <button
            onClick={handleExportSVG}
            title={t('drawing.exportSVG')}
            className="flex h-8 items-center gap-1 px-2.5 text-[11px] font-medium text-synergy-text-secondary transition-colors [@media(hover:hover)]:hover:bg-synergy-neutral-800/8 [@media(hover:hover)]:hover:text-synergy-text-primary"
          >
            <Download className="h-3 w-3" />
            <span>SVG</span>
          </button>
          <button
            onClick={handleExportPDF}
            title={t('drawing.exportPDF')}
            className="flex h-8 items-center gap-1 px-2.5 text-[11px] font-medium text-synergy-text-secondary transition-colors [@media(hover:hover)]:hover:bg-synergy-neutral-800/8 [@media(hover:hover)]:hover:text-synergy-text-primary"
          >
            <FileDown className="h-3 w-3" />
            <span>PDF</span>
          </button>
        </div>
      </div>

      {/* BOM Review Modal */}
      {metadata?.bom && (
        <BOMReviewModal
          open={bomModalOpen}
          onOpenChange={setBomModalOpen}
          bom={metadata.bom}
          onAccept={handleBOMAccept}
        />
      )}
    </div>
  );
}
